import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { PUSD, generateTokenUri, toBaseUnits, fromBaseUnits } from '@/lib/stablecoin';
import { toTokenAwareAddress, isTokenAwareAddress } from '@/lib/fulcrum';
import { useTokenPaymentDetection } from '@/hooks/useTokenPaymentDetection';

/**
 * PROOF OF CONCEPT — accept ParyonUSD (PUSD) stablecoin via CashTokens.
 *
 * Standalone test page at /pusd-poc. Not wired into the main POS flow yet —
 * this exists to validate the three unknowns before productionizing:
 *   1. Does a token-aware URI/QR prefill the right PUSD amount in a wallet?
 *   2. Is the z-address derivation correct?
 *   3. Does Fulcrum `tokens_only` detect the incoming payment?
 */
export function PusdPocPage() {
  useSeoMeta({
    title: 'PUSD Acceptance — Proof of Concept',
    description: 'Test accepting ParyonUSD (PUSD) CashToken payments via BCHPAY.Cash.',
  });

  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('1.00');
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<{ txid: string; amountUnits: string } | null>(null);

  const displayAmount = parseFloat(amount) || 0;

  // Derive token-aware address + URI (guarded — invalid addresses throw).
  const derived = useMemo(() => {
    if (!address.trim()) return null;
    try {
      const tokenAddr = toTokenAwareAddress(address);
      const alreadyTa = isTokenAwareAddress(address);
      const uri = displayAmount > 0
        ? generateTokenUri(address, PUSD, displayAmount, { label: 'BCHPAY.Cash PUSD test' })
        : null;
      return { tokenAddr, alreadyTa, uri, error: null as string | null };
    } catch (e) {
      return { tokenAddr: '', alreadyTa: false, uri: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [address, displayAmount]);

  const canWatch = started && !!derived?.tokenAddr && displayAmount > 0 && !result;

  const { status, error, debug, reset } = useTokenPaymentDetection(
    derived?.tokenAddr ?? '',
    PUSD,
    displayAmount,
    canWatch,
    (payment) => setResult(payment),
  );

  const expectedUnits = toBaseUnits(displayAmount, PUSD);

  const handleReset = () => {
    setStarted(false);
    setResult(null);
    reset();
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 py-8 px-4">
      <div className="max-w-md mx-auto space-y-5">
        {/* Header */}
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-400 text-xs font-bold">
            🧪 Proof of Concept
          </div>
          <h1 className="text-2xl font-black text-zinc-900 dark:text-zinc-50">Accept PUSD Stablecoin</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            ParyonUSD (PUSD) · CashToken on Bitcoin Cash
          </p>
        </div>

        {/* Config card */}
        {!started && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 space-y-4 shadow-sm">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
                Merchant BCH Address
              </label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="bitcoincash:q… (or z…)"
                className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-sm font-mono text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]/40"
              />
              <p className="text-[11px] text-zinc-400 leading-snug">
                Paste a normal q-address — we'll convert it to its token-aware z-form automatically.
                Your wallet must support CashTokens (e.g. Cashonize) to see received PUSD.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
                Amount (PUSD ≈ USD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-semibold">$</span>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputMode="decimal"
                  className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-sm font-semibold text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]/40"
                />
              </div>
            </div>

            {/* Derivation preview */}
            {derived?.error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                {derived.error}
              </div>
            )}
            {derived && !derived.error && (
              <div className="rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 px-3 py-2.5 space-y-1.5 font-mono text-[11px] break-all">
                <div>
                  <span className="text-zinc-400">token-aware addr: </span>
                  <span className="text-[#0AC18E]">{derived.tokenAddr}</span>
                </div>
                <div>
                  <span className="text-zinc-400">base units: </span>
                  <span className="text-zinc-700 dark:text-zinc-200">
                    {expectedUnits.toString()} ({fromBaseUnits(expectedUnits, PUSD)} PUSD)
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={() => setStarted(true)}
              disabled={!derived || !!derived.error || displayAmount <= 0}
              className="w-full py-3.5 rounded-xl text-sm font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Request PUSD Payment →
            </button>
          </div>
        )}

        {/* Payment / watching screen */}
        {started && !result && derived?.uri && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 space-y-4 shadow-sm">
            <div className="text-center">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Requesting</p>
              <p className="text-3xl font-black text-zinc-900 dark:text-zinc-50">
                {fromBaseUnits(expectedUnits, PUSD)} <span className="text-[#0AC18E]">PUSD</span>
              </p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-zinc-100 flex items-center justify-center">
              <QRCodeCanvas value={derived.uri} size={240} level="M" className="rounded-lg" />
            </div>

            <div className="rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 px-3 py-2 font-mono text-[10px] text-zinc-500 break-all">
              {derived.uri}
            </div>

            {/* Status */}
            <div className="flex items-center justify-center gap-3 py-1">
              {status === 'waiting' && (
                <>
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0AC18E] opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#0AC18E]" />
                  </span>
                  <span className="text-sm text-zinc-500">Watching for PUSD…</span>
                </>
              )}
              {status === 'error' && (
                <span className="text-sm text-red-500">{error ?? 'Detection error'}</span>
              )}
            </div>

            {/* Diagnostics */}
            <div className="rounded-xl bg-zinc-100 dark:bg-zinc-800 px-3 py-2 font-mono text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300 break-all space-y-0.5">
              <div>polls: {debug.polls}</div>
              <div>server: {debug.server || '—'}</div>
              <div>token UTXOs seen: {debug.lastTokenUtxos}</div>
              <div>expecting ≥ {(expectedUnits * 80n / 100n).toString()} units</div>
              <div>category: {PUSD.category.slice(0, 16)}…</div>
              <div className={debug.lastError ? 'text-red-500' : ''}>
                error: {debug.lastError || 'none'}
              </div>
            </div>

            <button
              onClick={handleReset}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Success */}
        {result && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border-2 border-[#0AC18E] p-6 space-y-4 shadow-sm text-center">
            <div className="w-16 h-16 rounded-full bg-[#0AC18E]/15 flex items-center justify-center mx-auto">
              <span className="text-3xl">✅</span>
            </div>
            <div>
              <h2 className="text-2xl font-black text-zinc-900 dark:text-zinc-50">PUSD Received!</h2>
              <p className="text-lg font-bold text-[#0AC18E]">
                {fromBaseUnits(BigInt(result.amountUnits), PUSD)} PUSD
              </p>
            </div>
            <a
              href={`https://bchexplorer.cash/tx/${result.txid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs font-mono text-[#0AC18E] hover:underline break-all"
            >
              {result.txid}
            </a>
            <button
              onClick={handleReset}
              className="w-full py-3.5 rounded-xl text-sm font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30"
            >
              New Test
            </button>
          </div>
        )}

        <div className="text-center">
          <Link to="/app" className="text-xs text-zinc-400 hover:text-[#0AC18E] transition-colors">
            ← Back to POS
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PusdPocPage;
